import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2 , Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DefinitionOfDoneForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: "",
    version: "1.0",
    date: new Date().toISOString().split("T")[0],
    prepared_by: "",
    general_criteria: [""],
    development_criteria: [""],
    testing_criteria: [""],
    documentation_criteria: [""],
    deployment_criteria: [""],
    approval_name: "",
    approval_role: ""
  });

  const getSampleData = () => ({
    project_name: "E-Commerce Platform",
    version: "2.0",
    date: new Date().toISOString().split("T")[0],
    prepared_by: "Scrum Team",
    general_criteria: [
      "User story meets all acceptance criteria",
      "Code is peer reviewed and approved",
      "No critical or high-severity bugs remain",
      "Product Owner has accepted the story"
    ],
    development_criteria: [
      "Code follows team coding standards",
      "Code is checked into version control",
      "Code compiles without errors or warnings",
      "No TODO comments left in code",
      "Logging and error handling implemented"
    ],
    testing_criteria: [
      "Unit tests written with >80% code coverage",
      "Integration tests pass",
      "Regression tests pass",
      "Performance testing completed (if applicable)",
      "Security scan completed with no critical issues"
    ],
    documentation_criteria: [
      "Technical documentation updated",
      "API documentation updated (if applicable)",
      "User documentation updated",
      "README updated with any new dependencies",
      "Changelog updated"
    ],
    deployment_criteria: [
      "Deployed to staging environment",
      "Smoke tests pass in staging",
      "Configuration changes documented",
      "Rollback plan documented",
      "Ready for production deployment"
    ],
    approval_name: "John Smith",
    approval_role: "Scrum Master"
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Definition of Done (DoD) Draft";
      const payload = {
        name: draftName,
        template_type: "Definition of Done (DoD)",
        template_id: "definition-of-done",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "definition-of-done", "Definition Of Done", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("definition-of-done", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/definition-of-done`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Definition_of_Done_${formData.project_name || "Document"}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Definition of Done (DoD)",
        formData,
        settings: activeSettings || {},
        templateType: "Definition of Done (DoD)",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateCriteriaItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addCriteriaItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeCriteriaItem = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const renderCriteriaSection = (label, field) => (
    <div className="space-y-2">
      <Label className="font-semibold">{label}</Label>
      {formData[field].map((item, index) => (
        <div key={index} className="flex gap-2">
          <Input 
            value={item} 
            onChange={(e) => updateCriteriaItem(field, index, e.target.value)} 
            placeholder={`${label.replace(" Criteria", "")} criterion`} 
          />
          <Button variant="ghost" size="icon" onClick={() => removeCriteriaItem(field, index)}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => addCriteriaItem(field)}>
        <Plus className="h-4 w-4 mr-2" /> Add Criterion
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Definition of Done (DoD)</h1>
                <p className="text-sm text-gray-500">Define completion criteria for work items</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Document Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Version</Label>
                <Input value={formData.version} onChange={(e) => setFormData({ ...formData, version: e.target.value })} placeholder="1.0" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
              </div>
              <div>
                <Label>Prepared By</Label>
                <Input value={formData.prepared_by} onChange={(e) => setFormData({ ...formData, prepared_by: e.target.value })} placeholder="Team name" />
              </div>
            </div>

            <Separator />

            {renderCriteriaSection("General Criteria", "general_criteria")}
            
            <Separator />
            
            {renderCriteriaSection("Development Criteria", "development_criteria")}
            
            <Separator />
            
            {renderCriteriaSection("Testing Criteria", "testing_criteria")}
            
            <Separator />
            
            {renderCriteriaSection("Documentation Criteria", "documentation_criteria")}
            
            <Separator />
            
            {renderCriteriaSection("Deployment Criteria", "deployment_criteria")}

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Approval Name</Label>
                <Input value={formData.approval_name} onChange={(e) => setFormData({ ...formData, approval_name: e.target.value })} placeholder="Approver name" />
              </div>
              <div>
                <Label>Approval Role</Label>
                <Input value={formData.approval_role} onChange={(e) => setFormData({ ...formData, approval_role: e.target.value })} placeholder="Scrum Master" />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DefinitionOfDoneForm;
